import {
  useDeleteFactorLimitMutation,
  useResumeFactorLimitMutation,
  useSuspendFactorLimitMutation,
} from '@/app/api';
import { FactorLimit } from '@/app/api/types';
import { getFetchEror } from '@/app/helpers';
import { eimzo } from '@/entities/eimzo';
import { useNotification } from '@/shared/contexts/NotificationContext';
import { Button, SxProps } from '@mui/material';
import { Stack } from '@mui/system';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '@/app/hooks';

interface Props {
  sx?: SxProps;
  openLimitModal: () => void;
  data: FactorLimit;
}
export const FactorLimitActions: FC<Props> = ({ sx, openLimitModal, data }) => {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const { profile, isUsbLog } = useAppSelector((state) => state.auth);

  const [suspendLimit, { isLoading: isSuspendLimitLoading }] =
    useSuspendFactorLimitMutation();
  const [resumeLimit, { isLoading: isResumeLimitLoading }] =
    useResumeFactorLimitMutation();
  const [deleteLimit, { isLoading: isDeleteLoading }] =
    useDeleteFactorLimitMutation();

  const handleSuspend = async () => {
    try {
      await suspendLimit({ id: data.unique }).unwrap();
      showNotification(t('successfully_suspended'), 'success');
    } catch (err) {
      showNotification(getFetchEror(err), 'error');
    }
  };

  const handleResume = async () => {
    try {
      await resumeLimit({ id: data.unique }).unwrap();
      showNotification(t('limit_successfully_resumed'), 'success');
    } catch (err) {
      showNotification(getFetchEror(err), 'error');
    }
  };

  const handleDelete = async () => {
    try {
      const signatureBody = {
        DocumentId: data.generalAgreement?.didoxId,
        SellerTin: profile?.tin || '',
      };
      const signature = await eimzo.sign(
        JSON.stringify(signatureBody),
        profile?.tin || '',
        isUsbLog
      );
      await deleteLimit({ id: data.unique, signature }).unwrap();
      showNotification(t('limit_successfully_deleted'), 'success');
      navigate('/factor/limits');
    } catch (err) {
      showNotification(getFetchEror(err), 'error');
    }
  };

  const isLimitActive = [
    'active',
    'generalAgreementSignatureExpected',
    'generalAgreementWasRejected',
    'generalAgreementExpired',
  ].includes(data.status);

  return (
    <Stack sx={sx} direction='row' spacing={2} justifyContent='flex-end'>
      {isLimitActive ? (
        <Button
          color='inherit'
          disabled={isSuspendLimitLoading}
          onClick={handleSuspend}
        >
          {t('set_on_pause')}
        </Button>
      ) : (
        <Button
          color='inherit'
          disabled={isResumeLimitLoading}
          onClick={handleResume}
        >
          {t('resume')}
        </Button>
      )}
      {isLimitActive && (
        <Button color='inherit' onClick={openLimitModal}>
          {t('change_limit')}
        </Button>
      )}
      <Button color='error' onClick={handleDelete} disabled={isDeleteLoading}>
        {t('delete')}
      </Button>
    </Stack>
  );
};
